import React from 'react';
import { Button } from './ui/button';
import { Check, X } from 'lucide-react';

const TIERS = [
  {
    id: 'standard',
    name: 'Standard',
    price: 39,
    badge: null,
    accent: 'border-gray-200',
    ctaText: 'Choose Standard',
    ctaClass: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    badge: 'Most Popular',
    accent: 'border-2',
    accentStyle: { borderColor: '#F5C100' },
    ctaText: 'Choose Pro',
    ctaClass: 'text-gray-900 font-semibold',
    ctaStyle: { backgroundColor: '#F5C100' },
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 149,
    badge: null,
    accent: 'border-gray-200',
    ctaText: 'Choose Elite',
    ctaClass: 'bg-blue-800 hover:bg-blue-900 text-white',
  },
];

const FEATURE_ROWS = [
  {
    feature: 'Best For',
    values: ['Getting HR organized', 'Growing businesses without HR', 'Extra HR support & guidance'],
    type: 'text',
  },
  {
    feature: 'HR Templates',
    values: ['50 Essential Templates', '245 Full Library', '245 Full Library'],
    type: 'text',
  },
  {
    feature: 'Downloads',
    values: ['10/month', '15/month', 'Unlimited'],
    type: 'text',
  },
  { feature: 'Guides & Checklists', values: [true, true, true], type: 'check' },
  {
    feature: 'Monthly HR Insider (tips, best practices, new resources)',
    values: [true, true, true],
    type: 'check',
  },
  { feature: 'New Templates & Updates', values: [true, true, true], type: 'check' },
  { feature: 'Compliance Alerts', values: [false, true, true], type: 'check' },
  { feature: 'Monthly HR Reminders', values: [false, true, true], type: 'check' },
  {
    feature: 'HR Resource Library',
    values: ['Basic Access', 'Full Access', 'Full Access'],
    type: 'text',
  },
  {
    feature: 'Ask DriveHR Email Support',
    values: [false, '3 requests/month (standard response)', '5 requests/month (priority response)'],
    type: 'mixed',
  },
  {
    feature: 'Document Review Credit',
    values: [false, false, '1 review/month'],
    type: 'mixed',
  },
  {
    feature: 'Early Access to New Tools & Features',
    values: [false, false, true],
    type: 'check',
  },
];

const Cell = ({ value, type, tierIndex }) => {
  if (type === 'check' || (type === 'mixed' && typeof value === 'boolean')) {
    return value ? (
      <Check className="w-5 h-5 mx-auto" style={{ color: '#1A56E8' }} aria-label="included" />
    ) : (
      <X className="w-5 h-5 mx-auto text-gray-300" aria-label="not included" />
    );
  }
  return (
    <span
      className={`text-sm ${tierIndex === 1 ? 'text-gray-900 font-medium' : 'text-gray-700'}`}
    >
      {value}
    </span>
  );
};

export const Memberships = () => {
  const handleChoose = (tierId) => {
    window.location.href = `/signup?tier=${tierId}`;
  };

  return (
    <section id="memberships" className="py-14 bg-gray-50" data-testid="memberships-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            DriveHR Membership Plans
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ongoing HR resources, compliance updates, and expert guidance. Pick the tier that fits your business. Cancel anytime.
          </p>
        </div>

        {/* Tier Cards Header — desktop/tablet only. Mobile uses the stacked card layout below. */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="hidden md:block" />
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-white rounded-xl p-6 text-center shadow-sm ${tier.accent}`}
              style={tier.accentStyle || {}}
              data-testid={`tier-header-${tier.id}`}
            >
              {tier.badge && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold rounded-full text-gray-900"
                  style={{ backgroundColor: '#F5C100' }}
                >
                  {tier.badge}
                </span>
              )}
              <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
              <div className="mt-2">
                <span className="text-4xl font-bold text-gray-900">${tier.price}</span>
                <span className="text-gray-500">/mo</span>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Table — desktop/tablet only */}
        <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden" data-testid="comparison-chart">
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody>
                {FEATURE_ROWS.map((row, rowIdx) => (
                  <tr
                    key={rowIdx}
                    className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="py-4 px-6 text-sm font-medium text-gray-900 align-top w-1/3">
                      {row.feature}
                    </td>
                    {row.values.map((value, tierIdx) => (
                      <td
                        key={tierIdx}
                        className={`py-4 px-6 text-center align-top ${tierIdx === 1 ? 'bg-yellow-50/40' : ''}`}
                        style={tierIdx === 1 ? { borderLeft: '2px solid #F5C100', borderRight: '2px solid #F5C100' } : {}}
                      >
                        <Cell value={value} type={row.type} tierIndex={tierIdx} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CTA Row — desktop/tablet only (mobile uses the stacked card layout) */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-4 gap-4 p-6 border-t border-gray-200">
            <div className="hidden md:block" />
            {TIERS.map((tier) => (
              <Button
                key={tier.id}
                size="lg"
                className={`w-full ${tier.ctaClass}`}
                style={tier.ctaStyle || {}}
                onClick={() => handleChoose(tier.id)}
                data-testid={`choose-${tier.id}-btn`}
              >
                {tier.ctaText}
              </Button>
            ))}
          </div>
        </div>

        {/* Mobile stacked layout — one card per tier with all features inline.
            Replaces the horizontally-scrolling table on phones (<768px). */}
        <div className="md:hidden space-y-6" data-testid="comparison-mobile">
          {TIERS.map((tier, tierIdx) => (
            <div
              key={tier.id}
              className={`relative bg-white rounded-xl p-6 shadow-sm ${tier.accent}`}
              style={tier.accentStyle || {}}
              data-testid={`mobile-tier-card-${tier.id}`}
            >
              {tier.badge && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold rounded-full text-gray-900"
                  style={{ backgroundColor: '#F5C100' }}
                >
                  {tier.badge}
                </span>
              )}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-gray-900">${tier.price}</span>
                  <span className="text-gray-500">/mo</span>
                </div>
              </div>
              <ul className="mt-6 space-y-3 divide-y divide-gray-100">
                {FEATURE_ROWS.map((row, rowIdx) => (
                  <li
                    key={rowIdx}
                    className="flex items-start justify-between gap-4 pt-3 first:pt-0"
                  >
                    <span className="text-sm text-gray-600 flex-1">{row.feature}</span>
                    <span className="text-sm text-gray-900 font-medium text-right shrink-0 max-w-[55%]">
                      <Cell value={row.values[tierIdx]} type={row.type} tierIndex={tierIdx} />
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                size="lg"
                className={`w-full mt-6 ${tier.ctaClass}`}
                style={tier.ctaStyle || {}}
                onClick={() => handleChoose(tier.id)}
                data-testid={`mobile-choose-${tier.id}-btn`}
              >
                {tier.ctaText}
              </Button>
            </div>
          ))}
        </div>

        {/* Fine Print */}
        <div className="mt-8 max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-500 leading-relaxed">
            Ask DriveHR Email Support includes general HR guidance, template questions, policy questions, HR process recommendations, and guidance using DriveHR resources. Memberships do not include live meetings, employee investigations, legal representation, dedicated HR management, complex HR projects, or full handbook creation/reviews. These are available separately through DriveHR Consulting Services.{' '}
            <a
              href="#consulting"
              className="text-blue-600 hover:text-blue-800 font-medium underline"
              data-testid="view-consulting-link"
            >
              View Consulting Services
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};
